import React, { useContext, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import InformativeCard from "components/ui/cards/InformativeCard";
import DemoDataBanner from "components/onboarding/DemoDataBanner";
import TransportTypeSetupModal from "components/onboarding/welcome-modals/TransportTypeSetupModal";
import SeedDataConfirmationModal from "components/onboarding/welcome-modals/SeedDataConfirmationModal";
import Button from "shared/components/FormElements/Button";
import { useNavigate } from "react-router-dom";
import {
  FaUser,
  FaSchool,
  FaBusAlt,
  FaExclamationTriangle,
  FaTruck,
  FaUserTie,
  FaCalendarAlt,
  FaUsers,
  FaUniversity,
  FaBuilding,
} from "react-icons/fa";
import { AuthContext } from "shared/context/auth-context";
import { StoreContext } from "shared/context/store-context";
import { useTransportTypeSetup } from "components/onboarding/transport-type-setup-hook";
import { useHttpClient } from "shared/hooks/http-hook";
import "./Dashboard.css";
import { PERMISSION_TYPES, PLANS, PLANS_META, COMPANY_FEATURES } from "@routebot/constants";
import { isInternationalTheme } from "config/ConfigUtils";

/** Stat card text: dark on light tints, light on deeper backgrounds */
const STAT = {
  fg: "#1a2332",
  onDeep: "#f8fafc",
};

const DashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const {
    students,
    parentOptions,
    personelOptions,
    drivers,
    vehicles,
    vehicleRoutes,
    bundledRoutes,
    standartPersonelServices,
    shifts,
    studentSchools,
    personelSchools,
  } = useContext(StoreContext);
  const { sendRequest } = useHttpClient();
  const [isResendingEmail, setIsResendingEmail] = useState(false);

  const {
    showTransportTypeModal,
    showSeedDataModal,
    selectedTransportType,
    isTransportTypeLoading,
    isSeedDataLoading,
    handleTransportTypeSelect,
    handleSeedDataAccept,
    handleSeedDataSkip,
  } = useTransportTypeSetup(auth.token, auth.client, auth.id);

  const currentPlanKey = auth.company?.plan || PLANS.FREE;
  const canAccessSubscription =
    (isInternationalTheme() && auth.permissions[PERMISSION_TYPES.ACCESS_TO_BILLING]) || auth.company?.isDemo;
  const showFreePlanUpgradeBanner = currentPlanKey === PLANS.FREE && canAccessSubscription;

  const currentPlanDisplayName = useMemo(() => {
    const meta = PLANS_META[currentPlanKey];
    const key = `plan.${String(currentPlanKey).toLowerCase()}.name`;
    return t(key, { defaultValue: meta?.name ?? currentPlanKey });
  }, [currentPlanKey, t]);

  /** Settings-only shortcuts (navbar + stat cards cover operational modules). Mirrors Settings tabs visibility. */
  const quickLinks = useMemo(() => {
    const links = [];
    const isDemo = auth.company?.isDemo;

    if (auth.permissions[PERMISSION_TYPES.ACCESS_TO_GIVE_PERMISSIONS] || isDemo) {
      links.push({ key: "teammates", to: "/settings?tab=teammates", label: t("dashboard.inviteTeamMember") });
    }
    if (canAccessSubscription) {
      links.push({ key: "subscription", to: "/settings?tab=subscription", label: t("subscriptionSettings") });
    }
    if (isInternationalTheme()) {
      links.push({ key: "help", to: "/settings?tab=help", label: t("helpSettings.tabLabel") });
    }
    return links;
  }, [auth.permissions, auth.company?.isDemo, canAccessSubscription, t]);

  const statGroups = useMemo(() => {
    const p = auth.permissions;

    const usersCards = [];
    if (p[PERMISSION_TYPES.ACCESS_TO_STUDENTS]) {
      usersCards.push({
        key: "students",
        title: t("dashboard.students"),
        subtitle: t("dashboard.totalStudents", { count: students?.length ?? 0 }),
        icon: <FaSchool />,
        background: "#7cb342",
        color: STAT.fg,
        onClick: () => navigate("/users?tab=students"),
      });
      usersCards.push({
        key: "parents",
        title: t("parents"),
        subtitle: t("dashboard.totalParents", { count: parentOptions?.length ?? 0 }),
        icon: <FaUsers />,
        background: "#039be5",
        color: STAT.onDeep,
        onClick: () => navigate("/users?tab=parents"),
      });
    }
    if (p[PERMISSION_TYPES.ACCESS_TO_PERSONELS]) {
      usersCards.push({
        key: "personels",
        title: t("employees"),
        subtitle: t("dashboard.totalPersonnel", { count: personelOptions?.length ?? 0 }),
        icon: <FaUser />,
        background: "#1e88e5",
        color: STAT.onDeep,
        onClick: () => navigate("/users?tab=personels"),
      });
    }
    if (p[PERMISSION_TYPES.ACCESS_TO_USERS]) {
      usersCards.push({
        key: "drivers",
        title: t("drivers"),
        subtitle: t("dashboard.totalDrivers", { count: drivers?.length ?? 0 }),
        icon: <FaUserTie />,
        background: "#c2185b",
        color: STAT.onDeep,
        onClick: () => navigate("/users?tab=drivers"),
      });
    }

    const routesCards = [];
    if (p[PERMISSION_TYPES.ACCESS_TO_STUDENTS]) {
      routesCards.push({
        key: "studentRoutes",
        title: t("vehicleRoutes"),
        subtitle: t("dashboard.activeServices", { count: vehicleRoutes?.length ?? 0 }),
        icon: <FaBusAlt />,
        background: "#00838f",
        color: STAT.onDeep,
        onClick: () => navigate("/services?tab=vehicleRoutes"),
      });
    }
    if (p[PERMISSION_TYPES.ACCESS_TO_STUDENTS] && auth.company?.features?.[COMPANY_FEATURES.BUNDLED_STUDENT_ROUTES]) {
      routesCards.push({
        key: "bundledRoutes",
        title: t("bundledRoutes.tabTitle"),
        subtitle: t("dashboard.activeServices", { count: bundledRoutes?.length ?? 0 }),
        icon: <FaBusAlt />,
        background: "#00acc1",
        color: STAT.onDeep,
        onClick: () => navigate("/services?tab=bundledRoutes"),
      });
    }
    if (p[PERMISSION_TYPES.ACCESS_TO_PERSONELS]) {
      routesCards.push({
        key: "personelRoutes",
        title: t("dashboard.employeeRoutes"),
        subtitle: t("dashboard.activeServices", { count: standartPersonelServices?.length ?? 0 }),
        icon: <FaBusAlt />,
        background: "#ef6c00",
        color: STAT.onDeep,
        onClick: () => navigate("/services?tab=standardPersonelServices"),
      });
    }
    if (p[PERMISSION_TYPES.ACCESS_TO_SHIFTS]) {
      routesCards.push({
        key: "shifts",
        title: t("shifts"),
        subtitle: t("dashboard.totalShifts", { count: shifts?.length ?? 0 }),
        icon: <FaCalendarAlt />,
        background: "#546e7a",
        color: STAT.onDeep,
        onClick: () => navigate("/shifts"),
      });
    }

    const clientsCards = [];
    if (p[PERMISSION_TYPES.ACCESS_TO_STUDENTS] && p[PERMISSION_TYPES.ACCESS_TO_SCHOOLS]) {
      clientsCards.push({
        key: "studentSchools",
        title: t("schools"),
        subtitle: t("dashboard.totalSchoolDestinations", { count: studentSchools?.length ?? 0 }),
        icon: <FaUniversity />,
        background: "#558b2f",
        color: STAT.onDeep,
        onClick: () => navigate("/schools?tab=schools"),
      });
    }
    if (p[PERMISSION_TYPES.ACCESS_TO_PERSONELS] && p[PERMISSION_TYPES.ACCESS_TO_SCHOOLS]) {
      clientsCards.push({
        key: "personelSchools",
        title: t("corporateClients"),
        subtitle: t("dashboard.totalCorporateClients", { count: personelSchools?.length ?? 0 }),
        icon: <FaBuilding />,
        background: "#5e35b1",
        color: STAT.onDeep,
        onClick: () => navigate("/schools?tab=companies"),
      });
    }

    const fleetCards = [];
    if (p[PERMISSION_TYPES.ACCESS_TO_INVENTORY]) {
      fleetCards.push({
        key: "vehicles",
        title: t("dashboard.vehicles"),
        subtitle: t("dashboard.totalVehicles", { count: vehicles?.length ?? 0 }),
        icon: <FaTruck />,
        background: "#6a1b9a",
        color: STAT.onDeep,
        onClick: () => navigate("/inventory"),
      });
    }

    const groups = [
      { id: "users", title: t("dashboard.groupYourUsers"), cards: usersCards },
      { id: "routes", title: t("dashboard.groupYourRoutes"), cards: routesCards },
      { id: "clients", title: t("dashboard.groupYourClients"), cards: clientsCards },
      { id: "fleet", title: t("dashboard.groupYourFleet"), cards: fleetCards },
    ];

    return groups.filter((g) => g.cards.length > 0);
  }, [
    auth.permissions,
    auth.company,
    t,
    students,
    parentOptions,
    personelOptions,
    drivers,
    vehicles,
    vehicleRoutes,
    bundledRoutes,
    standartPersonelServices,
    shifts,
    studentSchools,
    personelSchools,
    navigate,
  ]);

  const handleResendVerificationEmail = async () => {
    setIsResendingEmail(true);
    try {
      await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/clients/resend-verification`, "POST", null, {
        "Content-Type": "application/json",
        Authorization: "Bearer " + auth.token,
      });
      window.toast.success(t("dashboard.verificationEmailSent"));
    } catch (err) {
      window.toast.error(t("dashboard.verificationEmailError"));
    } finally {
      setIsResendingEmail(false);
    }
  };

  return (
    <div className="dashboard-container">
      <TransportTypeSetupModal
        show={showTransportTypeModal}
        onSelect={handleTransportTypeSelect}
        isLoading={isTransportTypeLoading}
      />

      <SeedDataConfirmationModal
        show={showSeedDataModal}
        onAccept={handleSeedDataAccept}
        onSkip={handleSeedDataSkip}
        isLoading={isSeedDataLoading}
        transportType={selectedTransportType}
      />

      {showTransportTypeModal || showSeedDataModal || isTransportTypeLoading || isSeedDataLoading ? null : (
        <>
          <h2 className="dashboard-welcome">{t("dashboard.welcome")}</h2>

          {showFreePlanUpgradeBanner && (
            <div className="dashboard-free-plan-banner">
              <div className="dashboard-free-plan-banner-text">
                <span className="dashboard-free-plan-label">{t("subscription.currentPlan")}</span>
                <span className="dashboard-free-plan-name">{currentPlanDisplayName}</span>
                <span className="dashboard-free-plan-hint">{t("dashboard.freePlanUpgradeHint")}</span>
              </div>
              <Button to="/settings?tab=subscription" size="small">
                {t("dashboard.upgradeSubscription")}
              </Button>
            </div>
          )}

          {auth.client && !auth.client.emailConfirmed && (
            <div
              className="dashboard-email-banner"
              style={{
                backgroundColor: "#fff3cd",
                border: "1px solid #ffeaa7",
                borderRadius: "8px",
                padding: "16px",
                margin: "auto",
                marginBottom: "24px",
                color: "#856404",
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
              }}
            >
              <FaExclamationTriangle
                style={{
                  fontSize: "20px",
                  marginTop: "2px",
                  flexShrink: 0,
                }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: "bold", marginBottom: "8px" }}>{t("dashboard.emailNotVerified")}</div>
                <div style={{ marginBottom: "12px" }}>{t("dashboard.emailNotVerifiedDescription")}</div>
                <Button
                  onClick={handleResendVerificationEmail}
                  isLoading={isResendingEmail}
                  disabled={isResendingEmail}
                  size="small"
                >
                  {t("dashboard.resendVerificationEmail")}
                </Button>
              </div>
            </div>
          )}

          {!auth.company.isDemo && (
            <div style={{ maxWidth: "600px", margin: "0 auto" }}>
              <DemoDataBanner />
            </div>
          )}

          {quickLinks.length > 0 && (
            <section
              className="dashboard-section dashboard-section--quick-links"
              aria-label={t("dashboard.quickLinks")}
            >
              <h3 className="dashboard-section-title">{t("dashboard.quickLinks")}</h3>
              <div className="dashboard-quick-links">
                {quickLinks.map((link) => (
                  <Button key={link.key} to={link.to} size="small" inverse>
                    {link.label}
                  </Button>
                ))}
              </div>
            </section>
          )}

          {statGroups.length > 0 && (
            <section className="dashboard-section" aria-label={t("dashboard.statsOverview")}>
              <h3 className="dashboard-section-title">{t("dashboard.statsOverview")}</h3>
              {statGroups.map((group) => (
                <div key={group.id} className="dashboard-stat-group-block">
                  <h4 className="dashboard-subsection-title">{group.title}</h4>
                  <div className="dashboard-stats-grid">
                    {group.cards.map((card) => (
                      <InformativeCard
                        key={card.key}
                        title={card.title}
                        subtitle={card.subtitle}
                        icon={card.icon}
                        background={card.background}
                        color={card.color}
                        onClick={card.onClick}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default DashboardPage;
